import { Routes } from '@angular/router';
import { DefaultLayoutComponent } from './layout';
import { AuthGuard } from './services/auth.guard';
import { modulePermissionGuard } from './module-permission.guard';
import { purchaseRequestEditGuard } from './purchase-request-edit.guard';
import { budgetAccessGuard } from './budget-access.guard';
import { activeSubscriptionGuard } from './services/active-subscription.guard';
import { MODULE_PERMISSION } from './constants';
import { SUBSCRIPTION_CATEGORY } from './enums';

export const routes: Routes = [
    {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full'
    },
    {
        path: '',
        component: DefaultLayoutComponent,
        data: {
            title: 'Home'
        },
        children: [
            {
                path: 'dashboard',
                canActivate: [AuthGuard],
                loadChildren: () => import('./views/dashboard/routes').then((m) => m.routes)
            },
            {
                path: 'theme',
                canActivate: [AuthGuard],
                loadChildren: () => import('./views/theme/routes').then((m) => m.routes)
            },
            {
                path: 'base',
                canActivate: [AuthGuard],
                loadChildren: () => import('./views/base/routes').then((m) => m.routes)
            },
            {
                path: 'buttons',
                canActivate: [AuthGuard],
                loadChildren: () => import('./views/buttons/routes').then((m) => m.routes)
            },
            {
                path: 'forms',
                canActivate: [AuthGuard],
                loadChildren: () => import('./views/forms/routes').then((m) => m.routes)
            },
            {
                path: 'icons',
                canActivate: [AuthGuard],
                loadChildren: () => import('./views/icons/routes').then((m) => m.routes)
            },
            {
                path: 'notifications',
                canActivate: [AuthGuard],
                loadChildren: () => import('./views/notifications/routes').then((m) => m.routes)
            },
            {
                path: 'widgets',
                canActivate: [AuthGuard],
                loadChildren: () => import('./views/widgets/routes').then((m) => m.routes)
            },
            {
                path: 'charts',
                canActivate: [AuthGuard],
                loadChildren: () => import('./views/charts/routes').then((m) => m.routes)
            },
            {
                path: 'pages',
                canActivate: [AuthGuard],
                loadChildren: () => import('./views/pages/routes').then((m) => m.routes)
            },
            {
                path: 'mat-table',
                canActivate: [AuthGuard],
                loadComponent: () => import('./table-grid/table-grid.component').then(m => m.TableGridComponent),
                data: {
                    title: 'Table Grid'
                }
            },
            {
                path: 'test-ag-grid',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/test-ag-grid/test-ag-grid.component').then(m => m.TestAgGridComponent),
                data: {
                    title: 'Eproject Clients'
                }
            },
            {
                path: 'content',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/content/content.component').then(m => m.ContentComponent),
                pathMatch: 'full',
                data: {
                    title: 'Content'
                }
            },
            {
                path: 'content/create',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/content/create/create-content.component').then(m => m.CreateContentComponent),
                pathMatch: 'full',
                data: {
                    title: 'Create Content'
                }
            },
            {
                path: 'content/:id/edit',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/content/edit/edit-content.component').then(m => m.EditContentComponent),
                pathMatch: 'full',
                data: {
                    title: 'Edit Content'
                }
            },
            {
                path: 'tutorials/:userType',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/tutorials/tutorials.component').then(m => m.TutorialsComponent),
                pathMatch: 'full',
                data: {
                    title: 'Tutorials'
                }
            },
            {
                path: 'submit-ticket',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/submit-ticket/submit-ticket.component').then(m => m.SubmitTicketComponent),
                pathMatch: 'full',
                data: {
                    title: 'Submit Ticket'
                }
            },
            {
                path: 'conversations',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/messaging/conversations/conversations.component').then(m => m.ConversationsComponent),
                data: {
                    title: 'routes.messages'
                }
            },
            {
                path: 'conversations/compose-new',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/messaging/compose-new-conversation/compose-new-conversation.component').then(m => m.ComposeNewConversationComponent),
                data: {
                    title: 'routes.composeNewMessage',
                    breadcrumb: [
                        { label: 'routes.messages', url: '/conversations' },
                        'breadcrumb.compose'
                    ]
                }
            },
            {
                path: 'conversations/:conversationId/details',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/messaging/conversation-details/conversation-details.component').then(m => m.ConversationDetailsComponent),
                data: {
                    title: 'routes.conversationDetails',
                    breadcrumb: [
                        { label: 'routes.messages', url: '/conversations' },
                        'breadcrumb.details'
                    ]
                }
            },
            {
                path: 'conversations/:conversationId/compose-reply',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/messaging/compose-reply-message/compose-reply-message.component').then(m => m.ComposeReplyMessageComponent),
                data: {
                    title: 'routes.replyMessage',
                    breadcrumb: [
                        { label: 'routes.messages', url: '/conversations' },
                        { label: 'breadcrumb.details', url: '/conversations/:conversationId/details' },
                        'breadcrumb.reply'
                    ]
                }
            },
            {
                path: 'eproject-clients',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/eproject-clients/eproject-clients/eproject-clients.component').then(m => m.EprojectClientsComponent),
                data: {
                    title: 'routes.eprojectClients'
                }
            },
            {
                path: 'create-eproject-clients',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/eproject-clients/create-eproject-clients/create-eproject-clients.component').then(m => m.CreateEprojectClientsComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.createEprojectClient',
                    breadcrumb: [
                        { label: 'routes.eprojectClients', url: '/eproject-clients' },
                        'breadcrumb.create'
                    ]
                }
            },
            {
                path: 'edit-eproject-clients/:clientId',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/eproject-clients/edit-eproject-clients/edit-eproject-clients.component').then(m => m.EditEprojectClientsComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.editEprojectClient',
                    breadcrumb: [
                        { label: 'routes.eprojectClients', url: '/eproject-clients' },
                        'breadcrumb.edit'
                    ]
                }
            },
            {
                path: 'eproject-client-actions/:clientId',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/eproject-clients/eproject-client-actions/eproject-client-actions.component').then(m => m.EprojectClientActionsComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.syncEprojectClient',
                    breadcrumb: [
                        { label: 'routes.eprojectClients', url: '/eproject-clients' },
                        'breadcrumb.sync'
                    ]
                }
            },
            {
                path: 'personal',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/personal/personal.component').then(m => m.PersonalComponent),
                data: {
                    title: 'routes.personal'
                }
            },
            {
                path: 'personal/:id/details',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/personal/details/personal-details.component').then(m => m.PersonalDetailsComponent),
                data: {
                    title: 'routes.personalDetails',
                    breadcrumb: [
                        { label: 'routes.personal', url: '/personal' },
                        'breadcrumb.details'
                    ]
                }
            },
            {
                path: 'personal/:id/edit',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/personal/edit/personal-details-edit.component').then(m => m.EditPersonalDetailsComponent),
                data: {
                    title: 'routes.editPersonalDetails',
                    breadcrumb: [
                        { label: 'routes.personal', url: '/personal' },
                        { label: 'breadcrumb.details', url: '/personal/:id/details' },
                        'breadcrumb.edit'
                    ]
                }
            },
            {
                path: 'companies',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/companies/companies/companies.component').then(m => m.CompaniesComponent),
                data: {
                    title: 'routes.companies'
                }
            },
            {
                path: 'users/:companyId',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/companies/list-users/list-users.component').then(m => m.ListUsersComponent),
                 data: {
                    title: 'routes.users',
                    breadcrumb: [
                        'routes.users'
                    ]
                }
            },
            {
                path: 'user/:userId',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/companies/edit-users/edit-users.component').then(m => m.EditUsersComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.editUser',
                    breadcrumb: [
                        'routes.users',
                        'breadcrumb.edit'
                    ]
                }
            },
            {
                path: 'system-notifications/:userId',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/system-notifications/system-notifications.component').then(m => m.SystemNotificationsComponent),
                pathMatch: 'full',
                data: {
                    title: 'System Notifications'
                }
            },
            {
                path: 'log-activities',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/log-activities/log-activities.component').then(m => m.LogActivitiesComponent),
                pathMatch: 'full',
                data: {
                    title: 'Log Activities'
                }
            },
            {
                path: 'user-logs',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/user-logs/user-logs.component').then(m => m.UserLogsComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.userLogs'
                }
            },
            {
                path: 'company-details/:companyId',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/companies/company-details/company-details.component').then(m => m.CompanyDetailsComponent),
                data: {
                    title: 'routes.companyDetails',
                    breadcrumb: [
                        { label: 'routes.companies', url: '/companies' },
                        'breadcrumb.details'
                    ]
                }
            },
            {
                path: 'company-users',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/company-users/company-users/company-users.component').then(m => m.CompanyUsersComponent),
                data: {
                    title: 'Company Users'
                }
            },
            {
                path: 'company-subscription/:companyId',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/company-subscription/company-subscription.component').then(m => m.CompanySubscriptionComponent),
                data: {
                    title: 'routes.companySubscription',
                    breadcrumb: [
                        { label: 'routes.companies', url: '/companies' },
                        'breadcrumb.subscription'
                    ]
                }
            },
            {
                path: 'company-subscription/:companyId/category/:categoryId/create',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/company-subscription/create/create-subscription.component').then(m => m.CreateSubscriptionComponent),
                data: {
                    title: 'routes.createCompanySubscription',
                    breadcrumb: [
                        { label: 'routes.companies', url: '/companies' },
                        { label: 'breadcrumb.subscription', url: '/company-subscription/:companyId' },
                        'breadcrumb.create'
                    ]
                }
            },
            {
                path: 'company-subscription/:companyId/category/:categoryId/edit',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/company-subscription/edit/edit-subscription.component').then(m => m.EditSubscriptionComponent),
                data: {
                    title: 'routes.editCompanySubscription',
                    breadcrumb: [
                        { label: 'routes.companies', url: '/companies' },
                        { label: 'breadcrumb.subscription', url: '/company-subscription/:companyId' },
                        'breadcrumb.edit'
                    ]
                }
            },
            {
                path: 'create-users/:companyId',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/company-users/create-users/create-users.component').then(m => m.CreateUsersComponent),
                pathMatch: 'full',
                data: {
                    title: 'Create User'
                }
            },
            {
                path: 'edit-user/:userId',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/company-users/edit-users/edit-users.component').then(m => m.EditUsersComponent),
                pathMatch: 'full',
                data: {
                    title: 'Edit User'
                }
            },
            {
                path: 'module-permissions',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/module-permissions/module-permissions.component').then(m => m.ModulePermissionsComponent),
                data: {
                    title: 'Module Permissions',
                }
            },
            {
                path: 'cost-manager',
                canActivate: [AuthGuard, modulePermissionGuard(MODULE_PERMISSION.COST_MANAGER)],
                loadComponent: () => import('./components/cost-manager/cost-category/cost-category-grid.component').then(m => m.CostCategoryGridComponent),
                data: {
                    title: 'routes.costManager'
                }
            },
            {
                path: 'cost-manager/category/:costCategoryId',
                canActivate: [AuthGuard, modulePermissionGuard(MODULE_PERMISSION.COST_MANAGER)],
                loadComponent: () => import('./components/cost-manager/cost-sub-category/cost-sub-category-grid.component').then(m => m.CostSubCategoryGridComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.costSubCategories',
                    breadcrumb: [
                        { label: 'routes.costManager', url: '/cost-manager' },
                        'routes.costSubCategories'
                    ]
                }
            },
            {
                path: 'cost-manager/category/:costCategoryId/import',
                canActivate: [AuthGuard, modulePermissionGuard(MODULE_PERMISSION.COST_MANAGER)],
                loadComponent: () => import('./components/cost-manager/cost-sub-category-import/cost-sub-category-import.component').then(m => m.CostSubCategoryImportComponent),
                pathMatch: 'full',
                data: {
                    title: 'Import Sub-Categories'
                }
            },
            {
                path: 'cost-manager/category/:costCategoryId/subCategory/:costSubCategoryId',
                canActivate: [AuthGuard, modulePermissionGuard(MODULE_PERMISSION.COST_MANAGER)],
                loadComponent: () => import('./components/cost-manager/cost-item/cost-item-grid.component').then(m => m.CostItemGridComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.costItems',
                    breadcrumb: [
                        { label: 'routes.costManager', url: '/cost-manager' },
                        'routes.costSubCategories',
                        'costManager.items'
                    ]
                }
            },
            {
                path: 'cost-manager/category/:costCategoryId/subCategory/:costSubCategoryId/item/:costItemId/purchase-details',
                canActivate: [AuthGuard, modulePermissionGuard(MODULE_PERMISSION.COST_MANAGER)],
                loadComponent: () => import('./components/cost-manager/cost-item-purchase-detail/cost-item-purchase-detail.component').then(m => m.CostItemPurchaseDetailComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.purchaseDetails',
                    breadcrumb: [
                        { label: 'routes.costManager', url: '/cost-manager' },
                        'routes.costSubCategories',
                        'costManager.items',
                        'routes.purchaseDetails'
                    ]
                }
            },
            {
                path: 'profile',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/user-profile/user-profile.component').then(m => m.UserProfileComponent),
                data: {
                    title: 'routes.userProfile',
                    breadcrumb: ['routes.userProfile']
                }
            },
            {
                path: 'profile/company-edit',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/user-profile/edit/edit-user-profile.component').then(m => m.EditUserProfileComponent),
                data: {
                    title: 'routes.editCompanyProfile',
                    breadcrumb: [
                        { label: 'routes.userProfile', url: '/profile' },
                        'breadcrumb.edit'
                    ]
                }
            },
            {
                path: 'unit-of-measurements',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/unit-of-measurements/unit-of-measurements.component').then(m => m.UnitOfMeasurementsComponent),
                data: {
                    title: 'routes.unitOfMeasurements'
                }
            },
            {
                path: 'countries',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/countries/countries.component').then(m => m.CountriesComponent),
                data: {
                    title: 'routes.countries'
                }
            },
            {
                path: 'currencies',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/currencies/currencies.component').then(m => m.CurrenciesComponent),
                data: {
                    title: 'routes.currencies'
                }
            },
            {
                path: 'tax-codes',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/tax-codes/tax-codes.component').then(m => m.TaxCodesComponent),
                data: {
                    title: 'routes.taxCodes'
                }
            },
            {
                path: 'property-types',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/property-types/property-types.component').then(m => m.PropertyTypesComponent),
                data: {
                    title: 'routes.propertyTypes'
                }
            },
            {
                path: 'business-entity-types',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/business-entity-types/business-entity-types.component').then(m => m.BusinessEntityTypesComponent),
                data: {
                    title: 'routes.businessEntityTypes'
                }
            },
            {
                path: 'cost-code',
                canActivate: [AuthGuard, modulePermissionGuard(MODULE_PERMISSION.COST_CODE)],
                loadComponent: () => import('./components/cost-code/cost-code-grid.component').then(m => m.CostCodeGridComponent),
                data: {
                    title: 'routes.costCode'
                }
            },
            {
                path: 'budgets',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/budgets/budgets.component').then(m => m.BudgetsComponent),
                data: {
                    title: 'routes.budgets'
                }
            },
            {
                path: 'budgets/create',
                canActivate: [AuthGuard, modulePermissionGuard(MODULE_PERMISSION.BUDGET), activeSubscriptionGuard(SUBSCRIPTION_CATEGORY.PROCUREX)],
                loadComponent: () => import('./components/budgets/create/create-budget.component').then(m => m.CreateBudgetComponent),
                pathMatch: 'full',
                data: {
                    title: 'Create Budget'
                }
            },
            {
                path: 'budget/:budgetId/edit',
                canActivate: [AuthGuard, budgetAccessGuard],
                loadComponent: () => import('./components/budgets/edit/edit-budget.component').then(m => m.EditBudgetComponent),
                pathMatch: 'full',
                data: {
                    title: 'Edit Budget'
                }
            },
            {
                path: 'budget/:budgetId/details',
                canActivate: [AuthGuard, budgetAccessGuard],
                loadComponent: () => import('./components/budgets/details/budget-details.component').then(m => m.BudgetDetailsComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.budgetDetails',
                    breadcrumb: [
                        { label: 'routes.budgets', url: '/budgets' },
                        'breadcrumb.details'
                    ]
                }
            },
            {
                path: 'budget/:budgetId/details/items/category/:categoryId/sub-category',
                canActivate: [AuthGuard, budgetAccessGuard],
                loadComponent: () => import('./components/budgets/details/imported/sub-category/budget-item-sub-category.component').then(m => m.BudgetItemSubCategoryComponent),
                pathMatch: 'full',
                data: {
                    title: 'Budget Item Sub-Category'
                }
            },
            {
                path: 'budget/:budgetId/details/items/category/:categoryId/sub-category/:subCategoryId/item',
                canActivate: [AuthGuard, budgetAccessGuard],
                loadComponent: () => import('./components/budgets/details/imported/item/budget-item.component').then(m => m.BudgetItemComponent),
                pathMatch: 'full',
                data: {
                    title: 'Budget Items'
                }
            },
            {
                path: 'budget/:budgetId/details/items/category/:categoryId/sub-category/:subCategoryId/item/:itemId/purchase-details',
                canActivate: [AuthGuard, budgetAccessGuard],
                loadComponent: () => import('./components/budgets/details/budget-item-purchase-detail/budget-item-purchase-detail.component').then(m => m.BudgetItemPurchaseDetailComponent),
                pathMatch: 'full',
                data: {
                    title: 'Budget Items Purchase Details'
                }
            },
            {
                path: 'budget/:budgetId/details/import/buildspace-resources',
                canActivate: [AuthGuard, budgetAccessGuard],
                loadComponent: () => import('./components/budgets/budget-import-buildspace-resources/budget-import-buildspace-resources.component').then(m => m.BudgetImportBuildspaceResourcesComponent),
                pathMatch: 'full',
                data: {
                    title: 'Import from Buildspace Resources'
                }
            },
            {
                path: 'budget/:budgetId/details/import/buildspace-awarded-contracts',
                canActivate: [AuthGuard, budgetAccessGuard],
                loadComponent: () => import('./components/budgets/budget-import-buildspace-awarded-contracts/budget-import-buildspace-awarded-contracts.component').then(m => m.BudgetImportBuildspaceAwardedContractsComponent),
                pathMatch: 'full',
                data: {
                    title: 'Import from Buildspace Awarded Contracts'
                }
            },
            {
                path: 'budget/:budgetId/details/import/category',
                canActivate: [AuthGuard, budgetAccessGuard],
                loadComponent: () => import('./components/budgets/details/import/category/category.component').then(m => m.BudgetItemImportCategoryComponent),
                pathMatch: 'full',
                data: {
                    title: 'Import Budget Items (Category)'
                }
            },
            {
                path: 'budget/:budgetId/details/import/category/:categoryId/sub-category',
                canActivate: [AuthGuard, budgetAccessGuard],
                loadComponent: () => import('./components/budgets/details/import/sub-category/sub-category.component').then(m => m.BudgetItemImportSubCategoryComponent),
                pathMatch: 'full',
                data: {
                    title: 'Import Budget Items (Sub-Category)'
                }
            },
            {
                path: 'budget/:budgetId/details/import/category/:categoryId/sub-category/:subCategoryId/item',
                canActivate: [AuthGuard, budgetAccessGuard],
                loadComponent: () => import('./components/budgets/details/import/item/item.component').then(m => m.BudgetItemImportItemComponent),
                pathMatch: 'full',
                data: {
                    title: 'Import Budget Items (Item)'
                }
            },
            {
                path: 'budget/:budgetId/userPermissions',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/budgets/user-permissions/budget-user-permission.component').then(m => m.BudgetUserPermissionComponent),
                data: {
                    title: 'routes.userPermissions',
                    breadcrumb: [
                        { label: 'routes.budgets', url: '/budgets' },
                        'routes.userPermissions'
                    ]
                }
            },
            {
                path: 'budget/:budgetId/locations',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/budget-locations/budget-locations.component').then(m => m.BudgetLocationsComponent),
                data: {
                    title: 'routes.budgetLocations',
                    breadcrumb: [
                        { label: 'routes.budgets', url: '/budgets' },
                        'breadcrumb.locations'
                    ]
                }
            },
            {
                path: 'budget/:budgetId/locations/create',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/budget-locations/create/create-budget-location.component').then(m => m.CreateBudgetLocationComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.createLocation',
                    breadcrumb: [
                        { label: 'routes.budgets', url: '/budgets' },
                        { label: 'breadcrumb.locations', url: '/budget/:budgetId/locations' },
                        'breadcrumb.create'
                    ]
                }
            },
            {
                path: 'budget/:budgetId/locations/edit/:budgetLocationId',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/budget-locations/edit/edit-budget-location.component').then(m => m.EditBudgetLocationComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.editLocation',
                    breadcrumb: [
                        { label: 'routes.budgets', url: '/budgets' },
                        { label: 'breadcrumb.locations', url: '/budget/:budgetId/locations' },
                        'breadcrumb.edit'
                    ]
                }
            },
            {
                path: 'budgets/:budgetId/purchaseOnBehalf',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/budgets/purchase-on-behalf/purchase-on-behalf.component').then(m => m.PurchaseOnBehalfComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.purchaseOnBehalf',
                    breadcrumb: [
                        { label: 'routes.budgets', url: '/budgets' },
                        'routes.purchaseOnBehalf'
                    ]
                }
            },
            {
                path: 'budgets/:budgetId/purchaseOnBehalfSeller/import',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/budgets/purchase-on-behalf-seller-add/purchase-on-behalf-seller-add.component').then(m => m.PurchaseOnBehalfSellerAddComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.importSeller',
                    breadcrumb: [
                        { label: 'routes.budgets', url: '/budgets' },
                        { label: 'routes.purchaseOnBehalf', url: '/budgets/:budgetId/purchaseOnBehalf' },
                        'breadcrumb.import'
                    ]
                }
            },
            {
                path: 'purchase-request',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/purchase-request/purchase-request.component').then(m => m.PurchaseRequestComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.purchaseRequest'
                }
            },
            {
                path: 'purchase-request/:purchaseRequestId/details',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/purchase-request/details/purchase-request-details.component').then(m => m.PurchaseRequestDetailsComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.purchaseRequestDetails',
                    breadcrumb: [
                        { label: 'routes.purchaseRequest', url: '/purchase-request' },
                        'breadcrumb.details'
                    ]
                }
            },
            {
                path: 'purchase-request/create',
                canActivate: [AuthGuard, activeSubscriptionGuard(SUBSCRIPTION_CATEGORY.PROCUREX)],
                loadComponent: () => import('./components/purchase-request/create/create-purchase-request.component').then(m => m.CreatePurchaseRequestComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.createPurchaseRequest',
                    breadcrumb: [
                        { label: 'routes.purchaseRequest', url: '/purchase-request' },
                        'breadcrumb.create'
                    ]
                }
            },
            {
                path: 'purchase-request/:purchaseRequestId/edit',
                canActivate: [AuthGuard, purchaseRequestEditGuard],
                loadComponent: () => import('./components/purchase-request/edit/edit-purchase-request.component').then(m => m.EditPurchaseRequestComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.editPurchaseRequest',
                    breadcrumb: [
                        { label: 'routes.purchaseRequest', url: '/purchase-request' },
                        { label: 'breadcrumb.details', url: '/purchase-request/:purchaseRequestId/details' },
                        'breadcrumb.edit'
                    ]
                }
            },
            // By Cost Manager
            {
                path: 'purchase-request/:purchaseRequestId/details/import/cost-manager/category',
                canActivate: [AuthGuard, purchaseRequestEditGuard],
                loadComponent: () => import('./components/purchase-request/details/import-by-cost-manager/category/category.component').then(m => m.PurchaseRequestItemImportCostManagerCategoryComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.importCostManagerItemsCategory',
                    breadcrumb: [
                        { label: 'routes.purchaseRequest', url: '/purchase-request' },
                        { label: 'breadcrumb.details', url: '/purchase-request/:purchaseRequestId/details' },
                        'breadcrumb.importCategory'
                    ]
                }
            },
            {
                path: 'purchase-request/:purchaseRequestId/details/import/cost-manager/category/:categoryId/sub-category',
                canActivate: [AuthGuard, purchaseRequestEditGuard],
                loadComponent: () => import('./components/purchase-request/details/import-by-cost-manager/sub-category/sub-category.component').then(m => m.PurchaseRequestItemImportCostManagerSubCategoryComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.importCostManagerItemsSubCategory',
                    breadcrumb: [
                        { label: 'routes.purchaseRequest', url: '/purchase-request' },
                        { label: 'breadcrumb.details', url: '/purchase-request/:purchaseRequestId/details' },
                        { label: 'breadcrumb.importCategory', url: '/purchase-request/:purchaseRequestId/details/import/cost-manager/category' },
                        'breadcrumb.importSubCategory'
                    ]
                }
            },
            {
                path: 'purchase-request/:purchaseRequestId/details/import/cost-manager/category/:categoryId/sub-category/:subCategoryId/item',
                canActivate: [AuthGuard, purchaseRequestEditGuard],
                loadComponent: () => import('./components/purchase-request/details/import-by-cost-manager/item/item.component').then(m => m.PurchaseRequestItemImportCostManagerItemComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.importCostManagerItemsItem',
                    breadcrumb: [
                        { label: 'routes.purchaseRequest', url: '/purchase-request' },
                        { label: 'breadcrumb.details', url: '/purchase-request/:purchaseRequestId/details' },
                        { label: 'breadcrumb.importCategory', url: '/purchase-request/:purchaseRequestId/details/import/cost-manager/category' },
                        { label: 'breadcrumb.importSubCategory', url: '/purchase-request/:purchaseRequestId/details/import/cost-manager/category/:categoryId/sub-category' },
                        'breadcrumb.importItem'
                    ]
                }
            },
            // By Budget
            {
                path: 'purchase-request/:purchaseRequestId/details/import/budget/:budgetId/category',
                canActivate: [AuthGuard, purchaseRequestEditGuard],
                loadComponent: () => import('./components/purchase-request/details/import-by-budget/category/category.component').then(m => m.PurchaseRequestItemImportBudgetCategoryComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.importBudgetItemsCategory',
                    breadcrumb: [
                        { label: 'routes.purchaseRequest', url: '/purchase-request' },
                        { label: 'breadcrumb.details', url: '/purchase-request/:purchaseRequestId/details' },
                        'breadcrumb.importCategory'
                    ]
                }
            },
            {
                path: 'purchase-request/:purchaseRequestId/details/import/budget/:budgetId/category/:categoryId/sub-category',
                canActivate: [AuthGuard, purchaseRequestEditGuard],
                loadComponent: () => import('./components/purchase-request/details/import-by-budget/sub-category/sub-category.component').then(m => m.PurchaseRequestItemImportBudgetSubCategoryComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.importBudgetItemsSubCategory',
                    breadcrumb: [
                        { label: 'routes.purchaseRequest', url: '/purchase-request' },
                        { label: 'breadcrumb.details', url: '/purchase-request/:purchaseRequestId/details' },
                        { label: 'breadcrumb.importCategory', url: '/purchase-request/:purchaseRequestId/details/import/budget/:budgetId/category' },
                        'breadcrumb.importSubCategory'
                    ]
                }
            },
            {
                path: 'purchase-request/:purchaseRequestId/details/import/budget/:budgetId/category/:categoryId/sub-category/:subCategoryId/item',
                canActivate: [AuthGuard, purchaseRequestEditGuard],
                loadComponent: () => import('./components/purchase-request/details/import-by-budget/item/item.component').then(m => m.PurchaseRequestItemImportBudgetItemComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.importBudgetItemsItem',
                    breadcrumb: [
                        { label: 'routes.purchaseRequest', url: '/purchase-request' },
                        { label: 'breadcrumb.details', url: '/purchase-request/:purchaseRequestId/details' },
                        { label: 'breadcrumb.importCategory', url: '/purchase-request/:purchaseRequestId/details/import/budget/:budgetId/category' },
                        { label: 'breadcrumb.importSubCategory', url: '/purchase-request/:purchaseRequestId/details/import/budget/:budgetId/category/:categoryId/sub-category' },
                        'breadcrumb.importItem'
                    ]
                }
            },
            {
                path: 'request-for-quotation',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/request-for-quotation/request-for-quotation.component').then(m => m.RequestForQuotationComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.requestForQuotation',
                    breadcrumb: ['routes.requestForQuotation']
                }
            },
            {
                path: 'request-for-quotation/:requestForQuotationId/details',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/request-for-quotation/details/request-for-quotation-details.component').then(m => m.RequestForQuotationDetailsComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.requestForQuotationDetails',
                    breadcrumb: [
                        { label: 'routes.requestForQuotation', url: '/request-for-quotation' },
                        'breadcrumb.details'
                    ]
                }
            },
            {
                path: 'request-for-quotation/create',
                canActivate: [AuthGuard, activeSubscriptionGuard(SUBSCRIPTION_CATEGORY.PROCUREX)],
                loadComponent: () => import('./components/request-for-quotation/create/create-request-for-quotation.component').then(m => m.CreateRequestForQuotationComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.createRequestForQuotation',
                    breadcrumb: [
                        { label: 'routes.requestForQuotation', url: '/request-for-quotation' },
                        'breadcrumb.create'
                    ]
                }
            },
            {
                path: 'request-for-quotation/:requestForQuotationId/edit',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/request-for-quotation/edit/edit-request-for-quotation.component').then(m => m.EditRequestForQuotationComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.editRequestForQuotation',
                    breadcrumb: [
                        { label: 'routes.requestForQuotation', url: '/request-for-quotation' },
                        { label: 'breadcrumb.details', url: '/request-for-quotation/:requestForQuotationId/details' },
                        'breadcrumb.edit'
                    ]
                }
            },
            {
                path: 'request-for-quotation/:requestForQuotationId/extend',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/request-for-quotation/details/extend/extend.component').then(m => m.RequestForQuotationExtendDateComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.extendRequestForQuotation',
                    breadcrumb: [
                        { label: 'routes.requestForQuotation', url: '/request-for-quotation' },
                        { label: 'breadcrumb.details', url: '/request-for-quotation/:requestForQuotationId/details' },
                        'breadcrumb.extend'
                    ]
                }
            },
            {
                path: 'request-for-quotation/:requestForQuotationId/details/import/seller',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/request-for-quotation/details/import/seller/seller.component').then(m => m.RequestForQuotationImportSellerComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.importSellerRequestForQuotation',
                    breadcrumb: [
                        { label: 'routes.requestForQuotation', url: '/request-for-quotation' },
                        { label: 'breadcrumb.details', url: '/request-for-quotation/:requestForQuotationId/details' },
                        'routes.importSeller'
                    ]
                }
            },
            {
                path: 'my-sellers',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/vendors/my-seller/my-seller.component').then(m => m.MySellerComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.mySellers'
                }
            },
            {
                path: 'my-sellers/:companyId/details',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/vendors/company-details/company-details.component').then(m => m.CompanyDetailsComponent),
                data: {
                    title: 'routes.companyDetails',
                    breadcrumb: [
                        { label: 'breadcrumb.mySellers', url: '/my-sellers' },
                        'breadcrumb.details'
                    ]
                },
            },
            {
                path: 'my-sellers/:companyId/users',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/vendors/list-users/list-users.component').then(m => m.ListUsersComponent),
                data: {
                    title: 'routes.users',
                    breadcrumb: [
                        { label: 'breadcrumb.mySellers', url: '/my-sellers' },
                        'routes.users'
                    ]
                },
            },
            {
                path: 'my-sellers/invite-seller',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/vendors/my-seller-invite-seller/my-seller-invite-seller.component').then(m => m.MySellerInviteSellerComponent),
                data: {
                    title: 'routes.inviteSeller',
                    breadcrumb: [
                        { label: 'breadcrumb.mySellers', url: '/my-sellers' },
                        'breadcrumb.invite'
                    ]
                }
            },
            {
                path: 'my-sellers/import',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/vendors/my-seller-import/my-seller-import.component').then(m => m.MySellerImportComponent),
                data: {
                    title: 'routes.importSellers',
                    breadcrumb: [
                        { label: 'routes.mySellers', url: '/my-sellers' },
                        'breadcrumb.import'
                    ]
                }
            },
            {
                path: 'my-clients',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/vendors/my-clients/my-clients.component').then(m => m.MyClientsComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.myBuyers'
                }
            },
            {
                path: 'my-clients/:companyId/details',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/vendors/company-details/company-details.component').then(m => m.CompanyDetailsComponent),
                data: {
                    title: 'Company Details',
                }
            },
            {
                path: 'my-products',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/products/products.component').then(m => m.MyProductsComponent),
                data: {
                    title: 'My Products'
                }
            },
            {
                path: 'create-product',
                canActivate: [AuthGuard, activeSubscriptionGuard(SUBSCRIPTION_CATEGORY.PROCUREX)],
                loadComponent: () => import('./components/products/create-product/create-product.component').then(m => m.CreateProductComponent),
                pathMatch: 'full',
                data: {
                    title: 'Create Product'
                }
            },
            {
                path: 'edit-product/:productId',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/products/edit-product/edit-product.component').then(m => m.EditProductComponent),
                pathMatch: 'full',
                data: {
                    title: 'Edit Product'
                }
            },
            {
                path: 'product-upload-image/:productId',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/products/upload-image/upload-image.component').then(m => m.UploadImageComponent),
                pathMatch: 'full',
                data: {
                    title: 'Upload Image'
                }
            },
            {
                path: 'settings',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/settings/settings.component').then(m => m.SettingComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.settings'
                }
            },
            {
                path: 'banner',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/settings/banner/banner.component').then(m => m.BannerComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.banner',
                    breadcrumb: [
                        { label: 'routes.settings', url: '/settings' },
                        'routes.banner'
                    ]
                }
            },
            {
                path: 'banner/create',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/settings/banner/create-banner/create-banner.component').then(m => m.CreateBannerComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.createBanner',
                    breadcrumb: [
                        { label: 'routes.settings', url: '/settings' },
                        { label: 'routes.banner', url: '/banner' },
                        'breadcrumb.create'
                    ]
                }
            },
            {
                path: 'banner/edit/:bannerId',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/settings/banner/show-banner/show-banner.component').then(m => m.ShowBannerComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.editBanner',
                    breadcrumb: [
                        { label: 'routes.settings', url: '/settings' },
                        { label: 'routes.banner', url: '/banner' },
                        'breadcrumb.edit'
                    ]
                }
            },
            {
                path: 'purchase-order',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/purchase-order/purchase-order.component').then(m => m.PurchaseOrderComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.purchaseOrder',
                    breadcrumb: ['routes.purchaseOrder']
                }
            },
            {
                path: 'purchase-order/create',
                canActivate: [AuthGuard, activeSubscriptionGuard(SUBSCRIPTION_CATEGORY.PROCUREX)],
                loadComponent: () => import('./components/purchase-order/create/create-purchase-order.component').then(m => m.CreatePurchaseOrderComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.createPurchaseOrder',
                    breadcrumb: [
                        { label: 'routes.purchaseOrder', url: '/purchase-order' },
                        'breadcrumb.create'
                    ]
                }
            },
            {
                path: 'purchase-order/show/:purchaseOrderId',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/purchase-order/show/show-purchase-order.component').then(m => m.ShowPurchaseOrderComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.purchaseOrderDetails',
                    breadcrumb: [
                        { label: 'routes.purchaseOrder', url: '/purchase-order' },
                        'breadcrumb.details'
                    ]
                }
            },
            {
                path: 'purchase-order/:purchaseOrderId/pdf-preview',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/purchase-order/show/pdf/purchase-order-pdf-preview.component').then(m => m.PdfPreviewPageComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.purchaseOrderPreview',
                    breadcrumb: [
                        { label: 'routes.purchaseOrder', url: '/purchase-order' },
                        { label: 'breadcrumb.details', url: '/purchase-order/show/:purchaseOrderId' },
                        'breadcrumb.preview'
                    ]
                }
            },
            {
                path: 'purchase-order/:purchaseOrderId/edit',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/purchase-order/edit/edit-purchase-order.component').then(m => m.EditPurchaseOrderComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.editPurchaseOrder',
                    breadcrumb: [
                        { label: 'routes.purchaseOrder', url: '/purchase-order' },
                        { label: 'breadcrumb.details', url: '/purchase-order/show/:purchaseOrderId' },
                        'breadcrumb.edit'
                    ]
                }
            },
            {
                path: 'invited-rfq',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/invited-rfq/invited-rfq.component').then(m => m.InvitedRFQComponent),
                pathMatch: 'full',
                data: {
                    title: 'Invited RFQ'
                }
            },
            {
                path: 'invited-rfq/:requestForQuotationId/details',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/invited-rfq/details/invited-rfq-details.component').then(m => m.InvitedRFQDetailsComponent),
                pathMatch: 'full',
                data: {
                    title: 'Invited RFQ Details'
                }
            },
            {
                path: 'public-rfq',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/public-rfq/public-rfq.component').then(m => m.PublicRFQComponent),
                pathMatch: 'full',
                data: {
                    title: 'Public RFQ'
                }
            },
            {
                path: 'public-rfq/:requestForQuotationId/details',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/public-rfq/details/public-rfq-details.component').then(m => m.PublicRFQDetailsComponent),
                pathMatch: 'full',
                data: {
                    title: 'Public RFQ Details'
                }
            },
            {
                path: 'purchase-order-seller',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/purchase-order/seller/purchase-order.component').then(m => m.SellerPurchaseOrderComponent ),
                pathMatch: 'full',
                data: {
                    title: 'Received Purchase Order'
                }
            },
            {
                path: 'purchase-order-seller/show/:purchaseOrderId',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/purchase-order/seller/show/show-purchase-order.component').then(m => m.ShowPurchaseOrderComponent),
                pathMatch: 'full',
                data: {
                    title: 'Received Purchase Order Details'
                }
            },
            {
                path: 'goods-receipt-note',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/goods-receipt-note/goods-receipt-note.component').then(m => m.GoodsReceiptNoteComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.goodsReceiptNote',
                    breadcrumb: ['routes.goodsReceiptNote']
                }
            },
            {
                path: 'goods-receipt-note/po/:purchaseOrderId',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/goods-receipt-note/po/goods-receipt-note-by-po.component').then(m => m.GoodsReceiptNoteByPOComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.goodsReceiptNoteByPO',
                    breadcrumb: [
                        { label: 'routes.purchaseOrder', url: '/purchase-order' },
                        { label: 'breadcrumb.details', url: '/purchase-order/show/:purchaseOrderId' },
                        'breadcrumb.goodsReceiptNote'
                    ]
                }
            },
            {
                path: 'goods-receipt-note/create',
                canActivate: [AuthGuard, activeSubscriptionGuard(SUBSCRIPTION_CATEGORY.PROCUREX)],
                loadComponent: () => import('./components/goods-receipt-note/create/create-goods-receipt-note.component').then(m => m.CreateGoodsReceiptNoteComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.createGoodsReceiptNote',
                    breadcrumb: [
                        { label: 'routes.goodsReceiptNote', url: '/goods-receipt-note' },
                        'breadcrumb.create'
                    ]
                }
            },
            {
                path: 'goods-receipt-note/:goodsReceiptNoteId/details',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/goods-receipt-note/details/goods-receipt-note-details.component').then(m => m.GoodsReceiptNoteDetailsComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.goodsReceiptNoteDetails',
                    breadcrumb: [
                        { label: 'routes.goodsReceiptNote', url: '/goods-receipt-note' },
                        'breadcrumb.details'
                    ]
                }
            },
            {
                path: 'payment/:purchaseOrderId',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/payment/payment.component').then(m => m.PaymentComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.payment',
                    breadcrumb: [
                        { label: 'routes.purchaseOrder', url: '/purchase-order' },
                        { label: 'breadcrumb.details', url: '/purchase-order/show/:purchaseOrderId' },
                        'breadcrumb.payment'
                    ]
                }
            },
            {
                path: 'payment/:purchaseOrderId/create',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/payment/create/create-payment.component').then(m => m.CreatePaymentComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.createPayment',
                    breadcrumb: [
                        { label: 'routes.purchaseOrder', url: '/purchase-order' },
                        { label: 'breadcrumb.details', url: '/purchase-order/show/:purchaseOrderId' },
                        { label: 'breadcrumb.payment', url: '/payment/:purchaseOrderId' },
                        'breadcrumb.create'
                    ]
                }
            },
            {
                path: 'subscription/category/setup',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/subscription/plan/category/subscription-category.component').then(m => m.SubscriptionCategoryComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.subscriptionPackagesSetup',
                    breadcrumb: [
                        { label: 'routes.subscriptionPlanSetup', url: '/subscription/plan/setup' },
                        'subscriptionCategory.setupCategories'
                    ]
                }
            },
            {
                path: 'subscription/tier/setup',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/subscription/plan/tier/subscription-tier.component').then(m => m.SubscriptionTierComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.subscriptionTierSetup',
                    breadcrumb: [
                        { label: 'routes.subscriptionPlanSetup', url: '/subscription/plan/setup' },
                        'subscriptionTier.setupTiers'
                    ]
                }
            },
            {
                path: 'subscription/plan/setup',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/subscription/plan/setup/subscription-plan-setup.component').then(m => m.SubscriptionPlanSetupComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.subscriptionPlanSetup'
                }
            },
            {
                path: 'subscription/plan/setup/create',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/subscription/plan/setup/create/create-subscription-plan.component').then(m => m.CreateSubscriptionPlanComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.createSubscriptionPlan',
                    breadcrumb: [
                        { label: 'routes.subscriptionPlanSetup', url: '/subscription/plan/setup' },
                        'breadcrumb.create'
                    ]
                }
            },
            {
                path: 'subscription/plan/setup/edit/:planId',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/subscription/plan/setup/edit/edit-subscription-plan.component').then(m => m.EditSubscriptionPlanComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.editSubscriptionPlan',
                    breadcrumb: [
                        { label: 'routes.subscriptionPlanSetup', url: '/subscription/plan/setup' },
                        'breadcrumb.edit'
                    ]
                }
            },
            {
                path: 'subscription/checkout/success',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/subscription/checkout/success/subs-checkout-success.component').then(m => m.SubsCheckoutSuccessComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.subscriptionCheckoutSuccess',
                    breadcrumb: [
                        { label: 'routes.subscription', url: '/subscription' },
                        'routes.subscriptionCheckoutSuccess'
                    ]
                }
            },
            {
                path: 'subscription',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/subscription/subscription.component').then(m => m.SubscriptionComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.subscription'
                }
            },
            {
                path: 'subscription/company/:companyId/category/:subscriptionCategoryId/plans',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/subscription/plan/subscription-plans.component').then(m => m.SubscriptionPlansComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.subscriptionPlans',
                    breadcrumb: [
                        { label: 'routes.subscription', url: '/subscription' },
                        'routes.subscriptionPlans'
                    ]
                }
            },
            {
                path: 'subscription/company/:companyId/category/:subscriptionCategoryId/history',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/subscription/history/subscription-history.component').then(m => m.SubscriptionHistoryComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.subscriptionHistory',
                    breadcrumb: [
                        { label: 'routes.subscription', url: '/subscription' },
                        'routes.subscriptionHistory'
                    ]
                }
            },
            {
                path: 'e-invoice',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/e-invoice/e-invoice.component').then(m => m.EInvoiceComponent),
                pathMatch: 'full',
                data: {
                    title: 'E-Invoice'
                }
            },
            {
                path: 'create-e-invoice',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/e-invoice/create-e-invoice/create-e-invoice.component').then(m => m.CreateEInvoiceComponent),
                pathMatch: 'full',
                data: {
                    title: 'Create E-Invoice'
                }
            },
            {
                path: 'emails/announcement',
                canActivate: [AuthGuard],
                loadComponent: () =>import('./components/emails/announcement-email/announcement-email.component').then(m => m.AnnouncementEmailComponent),
                pathMatch: 'full',
                data: {
                    title: 'routes.announcementEmail'
                }
            },              
            {
                path: 'property-listings',
                canActivate: [AuthGuard],
                loadComponent: () =>import('./components/propertyx/property-listings/property-listings.component').then(m => m.PropertyListingsComponent),
                pathMatch: 'full',
                data: { title: 'Property Listings' }
            },
            {
                path: 'property-listings/:id/details',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/propertyx/property-listings/details/property-listing-details.component').then(m => m.PropertyListingDetailsComponent),
                pathMatch: 'full',
                data: { title: 'Property Listing Details' }
            },    
            {
                path: 'property-listings/create',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/propertyx/property-listings/create/create-property-listings.component').then(m => m.CreatePropertyListingsComponent),
                pathMatch: 'full',
                data: { title: 'Add Property Listing' }
            },
            {
                path: 'property-listings/:id/edit',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/propertyx/property-listings/edit/edit-property-listings.component').then(m => m.EditPropertyListingsComponent),
                pathMatch: 'full',
                data: { title: 'Edit Property Listing' }
            },
            {
                path: 'property-listings/:id/images',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/propertyx/images/images.component').then(m => m.PropertyImagesComponent),
                pathMatch: 'full',
                data: { title: 'Property Images' }
            },
            {
                path: 'property-listings/:id/images/create',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/propertyx/images/create/create-images.component').then(m => m.CreatePropertyImagesComponent),
                pathMatch: 'full',
                data: { title: 'Add Property Image' }
            },
            {
                path: 'property-listings/:id/images/:imageId/edit',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/propertyx/images/edit/edit-images.component').then(m => m.EditPropertyImagesComponent),
                pathMatch: 'full',
                data: { title: 'Edit Property Image' }
            },         
            {
                path: 'property-listings/:propertyListingId/userPermissions',
                canActivate: [AuthGuard],
                loadComponent: () => import('./components/propertyx/property-listings/user-permissions/property-user-permission.component').then(m => m.PropertyUserPermissionComponent),
                data: {
                    title: 'User Permissions',
                }
            },     
        ]
    },
    {
        path: '404',
        loadComponent: () => import('./views/pages/page404/page404.component').then(m => m.Page404Component),
        data: {
            title: 'Page 404'
        }
    },
    {
        path: '500',
        loadComponent: () => import('./views/pages/page500/page500.component').then(m => m.Page500Component),
        data: {
            title: 'Page 500'
        }
    },
    {
        path: 'login',
        loadComponent: () => import('./views/pages/login/login.component').then(m => m.LoginComponent),
        data: {
            title: 'Login Page'
        }
    },
    {
        path: 'eproject-login/:token',
        loadComponent: () => import('./eproject-login/eproject-login.component').then(m => m.EprojectLoginComponent),
        data: {
            title: 'EProject Login'
        }
    },
    {
        path: 'buildspacepro-login/:token',
        loadComponent: () => import('./buildspace-pro-login/buildspace-pro-login.component').then(m => m.BuildspaceProLoginComponent),
        data: {
            title: 'Buildspace Pro Login'
        }
    },
    {
        path: 'confirm-user/:email',
        loadComponent: () => import('./confirm-user/confirm-user.component').then(m => m.ConfirmUserComponent),
        data: {
            title: 'Confirm User'
        }
    },
    // {
    //     path: 'register',
    //     loadComponent: () => import('./views/pages/register/register.component').then(m => m.RegisterComponent),
    //     data: {
    //         title: 'Register Page'
    //     }
    // },
    // {
    //     path: 'register/:buyerCompanyId',
    //     loadComponent: () => import('./views/pages/register/register.component').then(m => m.RegisterComponent),
    //     data: {
    //         title: 'Register Page'
    //     }
    // },
    {
        path: 'register/company',
        loadComponent: () => import('./views/pages/register/company/register-company.component').then(m => m.RegisterCompanyComponent),
        data: {
            title: 'Register Company Page'
        }
    },
    {
        path: 'register/company/:buyerCompanyId',
        loadComponent: () => import('./views/pages/register/company/register-company.component').then(m => m.RegisterCompanyComponent),
        data: {
            title: 'Register Company Page'
        }
    },
    {
        path: 'register/personal',
        loadComponent: () => import('./views/pages/register/personal/register-personal.component').then(m => m.RegisterPersonalComponent),
        data: {
            title: 'Register Personal Page'
        }
    },
    {
        path: 'home',
        loadComponent: () => import('./views/landing/landing.component').then(m => m.LandingComponent),
    },
    {
        path: 'forget-password',
        loadComponent: () => import('./views/pages/forget-password/forget-password.component').then(m => m.ForgetPasswordComponent),
        data: {
            title: 'Forget Password Page'
        }
    },
    {
        path: 'change-password/:email',
        loadComponent: () => import('./views/pages/change-password/change-password.component').then(m => m.ChangePasswordComponent),
        data: {
            title: 'Change Password Page'
        }
    },
    {
        path: 'products',
        loadComponent: () => import('./views/pages/products/products.component').then(m => m.ProductsComponent),
        data: {
            title: 'Products'
        }
    },
    {
        path: 'products/show/:productId',
        loadComponent: () => import('./views/pages/products/show/product-details.component').then(m => m.ProductDetailsComponent),
        data: {
            title: 'Product Details'
        }
    },
    { path: '**', redirectTo: 'dashboard' }
];
